import { getAllEndNotesOfChapter } from "../../../utils/get-chapter-endnotes";
import { find } from "lodash";
import { createElement, ReactElement } from "react";
import { PDFDocument } from "pdf-lib";
import { arabToRoman } from "roman-numbers";
import { pdf } from "@surge-global-engineering/rpdf-renderer";
import { Document, Page, Font } from "@surge-global-engineering/rpdf-renderer";
import {
  PdfChapter,
  PdfExpandedBook,
  PdfExtendedTOCSummaryItem,
  PdfSlateBaseParentBlockNode,
  PdfTheme,
} from "../../../components/Previewer/print/types";
import {
  centimetersToPt,
  getAdjustedBookDimensions, getImages, getFormattedChapter, hasFulBleedImage
} from "../../../components/Previewer/print/helpers";
import { fallBackFontFamilies, getFonts } from "../../../components/Previewer/print/configs";
import {
  Chapter,
  ChapterProps,
} from "../../../components/Previewer/print/renderers";
import { generateToc } from "../../../utils/toc";
import { notifyStatusChange, renderReadableStreamArrayBuffer } from "../../helpers/common";
import { BookCacheStoreSnapshot, PDFReadyChapterData, Task } from "../../types";
import { shouldIncludeChapterInPrint } from "../../../utils/helper";
import { getChapterNumber, isNumberedChapter } from "../../../utils/chapter-numbering";

const bootstrapPDFRenderer = async (hyphens: boolean): Promise<void> => {
  // register custom fonts
  const fonts = await getFonts();

  /**
   * Clear the fonts registry before registering new fonts
   */
  Font.clear();
  
  fonts.forEach((font) => {
    Font.register(font);
  });

  // register hyphens for the book
  if (hyphens) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Font.registerHyphenationCallback(null as any);
  } else {
    Font.registerHyphenationCallback((word) => [word]);
  }
};

const parseEmptyDoc = (theme: PdfTheme): ReactElement => {
  return createElement(
    Document,
    {},
    createElement(Page, {
      size: {
        width: centimetersToPt(theme.properties.trim.width),
        height: centimetersToPt(theme.properties.trim.height),
      },
    })
  );
};

const getChapterFromCache = async (snapshot: BookCacheStoreSnapshot, chapterId: string): Promise<ArrayBuffer | null> => {
  const blobUrl = snapshot[chapterId];
  if (blobUrl) {
    console.log("Using cache for", chapterId);
    const response = await fetch(blobUrl);
    return response.arrayBuffer();
  }
  return null;
};

const renderToPDFBuffer = async (tree: ReactElement): Promise<ArrayBuffer> => {
  const stream = await pdf(tree).toBuffer();

  return await renderReadableStreamArrayBuffer(stream);
};

const generateExtendedTOCSummary = (
  book: PdfExpandedBook,
  chapterPageNumberTexts: { [chapterId: string]: string }
) => {
  // generateTOC requires frontmatter. frontmater<-book.chapters is intentional
  //TODO:Verify --

  // generateTOC summary for all the chapters of the book
  // isExtendedSummary flag is used to identify if we need to get the tocSummary for all the chapters of the book
  const isExtendedSummary = true;  
  const _extendedTOCSummary = generateToc({ ...book, frontMatter: book.chapters }, book.chapters as IChapterStore.Chapter[], "print", undefined, isExtendedSummary).map((item) => {
    const chapter = (book.chapters || []).find(
      ({ _id }) => _id === item.itemId
    );
    if (!chapter) {
      throw new Error(`Chapter ${item.itemId} is missing`);
    }
    const extendedSummary: PdfExtendedTOCSummaryItem = {
      ...item,
      pageNumberText: chapterPageNumberTexts[chapter._id] || "",
    };

    return extendedSummary;
  });
  return _extendedTOCSummary;
};

const generateChapter = async (
  chapterToProcess: PDFReadyChapterData,
  tocSummeryData: PdfExtendedTOCSummaryItem[],
  cacheStoreSnapshot: BookCacheStoreSnapshot,
): Promise<PDFDocument> => {
  const {
    chapter,
    book,
    chapters,
    theme,
    lastPageNumber,
    chapterNumber,
  } = chapterToProcess;
  const cacheBuffer = await getChapterFromCache(cacheStoreSnapshot, chapter._id);
  
  if (cacheBuffer) {
    return await PDFDocument.load(cacheBuffer);
  }

  const tocSummary = generateToc(book, chapters, "print", chapter).map((item) => {
    const tocData = tocSummeryData?.find(({ itemId }) => itemId === item.itemId);
    if (tocData) {
      const { pageNumberText } = tocData;
      return {
        ...item,
        pageNumberText: item.chapterType === "volume" ? "" : pageNumberText,
      };
    }
    
    return {
      ...item,
      pageNumberText: "#",
    };
  });
    
  /**
   * getting the endNoteOffset for book level endnote numbering
   *  - need to recalculate the endNoteoffset 
   */
  let endNoteOffset = 0;

  if(theme.properties.notesMode==="END_OF_BOOK"){

    for (const chap of chapters) {
      if(chap._id===chapter._id)break;
      endNoteOffset += getAllEndNotesOfChapter(chap.children as PdfSlateBaseParentBlockNode[]).length;
    }
  
  }

  const chapterProps: ChapterProps = {
    chapter: getFormattedChapter(chapter),
    book,
    chapters,
    theme,
    endNoteOffset,
    chapterNumber,
    tocSummary,
  };
  
  const parsedChapter = createElement(Chapter, chapterProps);
  const parsedDocument = createElement(
    Document,
    { pageNumberOffset: lastPageNumber },
    parsedChapter
  );


  let arrayBuffer = await renderToPDFBuffer(parsedDocument);
  let pdfChap = await PDFDocument.load(arrayBuffer);

  /**
   * If chapter: is an image chapter | has a fullbleed image | can have light text
   * Create a separate pdf doc and replace the first pdf first page with the second one
   */
  let secondarrayBuffer: ArrayBuffer | null = null;
  const lightText = theme.properties.image.headerTextColor === "light";
  if (hasFulBleedImage(chapter, theme) || lightText || chapter.type === "image") {
    const parsedSecondChapter = createElement(Chapter, { ...chapterProps, assumeUsingOnlyFirstPage: true, firstPageLightText: lightText });
    const parsedSecondDocument = createElement(
      Document,
      { pageNumberOffset: lastPageNumber },
      parsedSecondChapter
    );
    secondarrayBuffer = await renderToPDFBuffer(parsedSecondDocument);
  }

  if (secondarrayBuffer) {
    const secondPDFChapter = await PDFDocument.load(secondarrayBuffer);
    const [secondPDFFirstPage] = await pdfChap.copyPages(secondPDFChapter, [0]);
    pdfChap.removePage(0);
    pdfChap.insertPage(0, secondPDFFirstPage);
  }

  if ((chapter.type === "copyrights") && (pdfChap.getPageCount() == 1)) {
    const parsedCopyrightChapter = createElement(Chapter,
      { ...chapterProps, isSinglePageCopyright: true });
    const parsedCopyrightDocument = createElement(
      Document,
      { pageNumberOffset: lastPageNumber },
      parsedCopyrightChapter
    );

    arrayBuffer = await renderToPDFBuffer(parsedCopyrightDocument);
    pdfChap = await PDFDocument.load(arrayBuffer);
  }

  // IF image chapter has more than 1 page remove other pages
  if (chapter.type === "image") {
    while (pdfChap.getPageIndices().length > 1) {
      pdfChap.removePage(1);
    }
  }

  return pdfChap;
};

const getEmptyPage = async (srcPdfDocument: PDFDocument, theme: PdfTheme, width: number, height: number) => {
  const emptyPageElement = parseEmptyDoc({ ...theme, properties: {...theme.properties, trim: { width, height }}});
  const emptyPageBuff = await renderToPDFBuffer(emptyPageElement);
  const emptyPagePdfChap = await PDFDocument.load(emptyPageBuff);
  const [emptyPage] = await srcPdfDocument.copyPages(
    emptyPagePdfChap,
    emptyPagePdfChap.getPageIndices()
  );
  return emptyPage;
};

const generateBookUrl = async (
  book: PdfExpandedBook,
  theme: PdfTheme,
  // styles: PdfThemeBase | null,
  cacheStoreSnapshot: BookCacheStoreSnapshot,
  task: Task,
  // customThemeBuilderView: string,
  // individualChapterImage: boolean,
  // images: CustomImages,
  // customThemeHeaders: PdfCustomThemeHeader | null,
): Promise<string> => {
  bootstrapPDFRenderer(theme.properties.paragraph.hyphens);

  let lastPageNumber = 0;
  let lastChapterNumber = 0; // used only within the body
  let tocPageNumber = 0;
  let tocChapterNumber = 0;

  const tocChaptersInfo: {
    [key: string]: {
      tocPageNumber: number;
      tocChapterNumber: number;
      allPageNumber: number;
    };
  } = {};
  const tocSummary = generateExtendedTOCSummary(book, {}); 
  const finalPDF = await PDFDocument.create();

  const { width, height } = getAdjustedBookDimensions(theme, book);

  // generate frontmatter
  console.log("Generating front matter");
  if (book.frontMatterIds) {
    for (let i = 0; i < book.frontMatterIds.length; i++) {
      const chapter = find(book.chapters, ["_id", book.frontMatterIds[i]]);
      if (!chapter || !shouldIncludeChapterInPrint(chapter)) continue;

      console.log("Generating chapter", chapter.type);
      // check for page side and add blank pages as necessary
      if (chapter.startOn) {
        if (
          (chapter.startOn === "left" &&
            lastPageNumber !== 0 &&
            lastPageNumber % 2 === 0) ||
          (chapter.startOn === "right" &&
            lastPageNumber !== 0 &&
            lastPageNumber % 2 !== 0)
        ) {
          const emptyPage = await getEmptyPage(finalPDF, theme, width, height);
          finalPDF.addPage(emptyPage);
          lastPageNumber++;
        }
      }

      //TODO:BODY --
      const pdfReadyChapterData: PDFReadyChapterData = {
        chapter,
        book,
        chapters: book.chapters,
        theme,
        lastPageNumber,
        endNoteOffset:0,// this will reassign inside `generateChapter` 
        chapterNumber: getChapterNumber(chapter, book),
      };

      const pdfChap = await generateChapter(pdfReadyChapterData, tocSummary, cacheStoreSnapshot);

      const copiedPages = await finalPDF.copyPages(
        pdfChap,
        pdfChap.getPageIndices()
      );
      copiedPages.forEach((page) => {
        finalPDF.addPage(page);
      });

      if (chapter.type === "toc") {
        tocChaptersInfo[chapter._id] = {
          tocPageNumber: lastPageNumber + 1,
          tocChapterNumber: lastChapterNumber + 1,
          allPageNumber: lastPageNumber + 1,
        };
      }

      // populate the page number into TOC
      const tocSummaryEntry = tocSummary.find(
        ({ itemId }) => itemId === chapter._id
      );
      if (tocSummaryEntry) {
        tocSummaryEntry.pageNumberText = `${arabToRoman(lastPageNumber + 1)}`;
      }
      //TODO:BODY --
      lastPageNumber += pdfChap.getPageCount();

      const progress =
        i /
        (book.frontMatterIds.length +
          book.chapterIds.length);
      await notifyStatusChange(task, "status", `Frontmatter ${i} compiled`, {
        progress,
      });
    }
  }

  // to end frontmatter always on a left side page
  if (lastPageNumber !== 0 && lastPageNumber % 2 !== 0) {
    const emptyPage = await getEmptyPage(finalPDF, theme, width, height);
    finalPDF.addPage(emptyPage);
    lastPageNumber++;
  }

  let allPageNumber = lastPageNumber;
  lastPageNumber = 0;

  let isFirstBodyChapter = true;

  // generate body
  console.log("Generating body");
  if (book.chapterIds) {
    for (let i = 0; i < book.chapterIds.length; i++) {
      const chapter = find(book.chapters, ["_id", book.chapterIds[i]]);
      if (!chapter || !shouldIncludeChapterInPrint(chapter)) continue;

      if (chapter.type === "endnotes" && theme.properties.notesMode !== "END_OF_BOOK") continue;

      // if starting side is not mentioned, default to the right side
      if (isFirstBodyChapter || !chapter.startOn) {
        chapter.startOn = "right";
        isFirstBodyChapter = false;
      }

      // check for page side and add blank pages as necessary
      if (
        (chapter.startOn === "left" &&
          lastPageNumber !== 0 &&
          lastPageNumber % 2 === 0) ||
        (chapter.startOn === "right" &&
          lastPageNumber !== 0 &&
          lastPageNumber % 2 !== 0)
      ) {
        const emptyPage = await getEmptyPage(finalPDF, theme, width, height);
        finalPDF.addPage(emptyPage);
        lastPageNumber++;
        allPageNumber++;
      }

      // TODO:BODY --
      const pdfReadyChapterData: PDFReadyChapterData = {
        chapter,
        book,
        chapters: book.chapters,
        theme,
        lastPageNumber,
        endNoteOffset:0,// this will reassign inside `generateChapter` 
        chapterNumber: getChapterNumber(chapter, book),
      };

      if(isNumberedChapter(chapter, book)){
        lastChapterNumber += 1;
      }

      const pdfChap = await generateChapter(pdfReadyChapterData, tocSummary, cacheStoreSnapshot);

      const copiedPages = await finalPDF.copyPages(
        pdfChap,
        pdfChap.getPageIndices()
      );
      copiedPages.forEach((page) => {
        finalPDF.addPage(page);
      });

      if (chapter.type === "toc") {
        tocChaptersInfo[chapter._id] = {
          tocPageNumber: lastPageNumber + 1,
          tocChapterNumber: lastChapterNumber + 1,
          allPageNumber: allPageNumber + 1,
        };
      }

      // populate the page number into TOC
      const tocSummaryEntry = tocSummary.find(
        ({ itemId }) => itemId === chapter._id
      );
      if (tocSummaryEntry) {
        tocSummaryEntry.pageNumberText = `${lastPageNumber + 1}`;
      }
      //TODO:BODY --
      lastPageNumber += pdfChap.getPageCount();
      allPageNumber += pdfChap.getPageCount();

      const progress =
        (book.frontMatterIds.length + i) /
        (book.frontMatterIds.length +
          book.chapterIds.length);
      await notifyStatusChange(task, "status", `Chapter ${i} compiled`, {
        progress,
      });
    }
  }

  for (const tocChapterId of Object.keys(tocChaptersInfo)) {
    // re-generate the TOC
    const tocChapter = book.chapters?.find(
      (chapter) => chapter._id === tocChapterId
    ) as PdfChapter;
    if (!tocChapter) {
      throw new Error("TOC Chapter is missing!");
    }

    tocPageNumber = tocChaptersInfo[tocChapterId].allPageNumber;
    tocChapterNumber = tocChaptersInfo[tocChapterId].tocChapterNumber;

    const chapter = find(book.chapters, ["_id", tocChapterId]);

    if (!chapter) {
      throw new Error("Chapter not found");
    }

    // if starting side is not mentioned, default to the right side
    if (!chapter.startOn) {
      chapter.startOn = "right";
    }

    //TODO:BODY --
    const pdfReadyChapterData: PDFReadyChapterData = {
      chapter,
      book,
      chapters: book.chapters,
      theme,
      lastPageNumber: tocChaptersInfo[tocChapterId].tocPageNumber - 1,
      endNoteOffset: 0,// this will reassign inside `generateChapter` 
      chapterNumber: getChapterNumber(chapter, book),
    };

    const pdfChap = await generateChapter(pdfReadyChapterData, tocSummary, cacheStoreSnapshot);

    const copiedPages = await finalPDF.copyPages(
      pdfChap,
      pdfChap.getPageIndices()
    );
    copiedPages.forEach(() => {
      // drop the old page
      finalPDF.removePage(tocPageNumber - 1);
    });
    copiedPages.forEach((page, index) => {
      // finalPDF.addPage(page);
      finalPDF.insertPage(tocPageNumber - 1 + index, page);
    });
  }

  // insert a blank page to the beginning
  // finalPDF.insertPage(0, emptyPage);

  console.log("Extracting the PDF to a buffer");
  const buff = await finalPDF.save();
  const blob = new Blob([buff]);
  const url = URL.createObjectURL(blob);

  console.log("PDF creation complete. Access on", url);

  return url;
};

export { generateBookUrl };
